import {
  QueryHookOptions,
  useQuery,
  MutationHookOptions,
  MutationTuple,
  useMutation,
  useLazyQuery,
  OperationVariables,
  QueryResult,
  QueryTuple,
  ApolloError,
  DocumentNode,
} from '@apollo/client';
import { OperationDefinitionNode } from 'graphql';
import { useRef, useEffect, useMemo } from 'react';

import { generateTraceId } from 'approot/shared/api/auth/generate-trace-id';
import { getClassToDoOrStudentToken } from 'approot/student-class-to-do/student-class-to-do.utils';
import { GRAPHQL_REQUEST_TRACE_ID, POLLING_QUERIES } from 'global/constants';
import { AnyObject } from 'webclient.constants';
import { emitStudentExtendSessionStorageEvent } from 'lib/session/session-storage';
import { getSessionToken } from 'lib/session/get-session-token';

export const getStudentHeaders = (traceId: string) => {
  const token = getClassToDoOrStudentToken();

  return {
    [GRAPHQL_REQUEST_TRACE_ID]: traceId,
    authorization: token ? `Bearer ${token}` : undefined,
  };
};

export const setupOptions = <
  TData extends any,
  TVariables extends OperationVariables = OperationVariables,
>(
  traceId: string,
  options?: QueryHookOptions<TData, TVariables>
) => {
  const newHeaders: AnyObject = getStudentHeaders(traceId);
  const missingToken = !newHeaders.authorization;

  const newOptions: QueryHookOptions<TData, TVariables> = {
    ...options,
    context: { headers: newHeaders },
    skip: missingToken,
  };

  // if options are provided, we need to merge the headers and skip if necessary
  if (options) {
    const { context, skip } = options;

    if (context?.headers) {
      newOptions.context = { ...context, headers: newHeaders };
    }

    if (skip) {
      newOptions.skip = skip;
    }
  }

  return newOptions;
};

export const setupMutationOptions = <
  TData extends any,
  TVariables extends OperationVariables = OperationVariables,
>(
  traceId: string,
  options?: MutationHookOptions<TData, TVariables>
) => {
  const newHeaders: AnyObject = getStudentHeaders(traceId);

  const newOptions: MutationHookOptions<TData, TVariables> = {
    ...options,
    context: { headers: newHeaders },
  };

  // if context headers are provided, we need to merge the headers
  if (options?.context?.headers) {
    newOptions.context = { ...options.context, headers: newHeaders };
  }

  return newOptions;
};

export const useStudentQuery = <
  TData extends any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode,
  options?: QueryHookOptions<TData, TVariables>
): QueryResult<TData, TVariables> => {
  const generatedTraceId = useMemo(() => generateTraceId(), []);

  // There is a bug where error is lost after re-renders, so we hold onto it in case it disappears
  // TODO: remove the logic and return the straight response when upgrading Apollo
  const latestError = useRef<ApolloError>(undefined);
  const { data, loading, error, ...response } = useQuery<TData, TVariables>(
    query,
    setupOptions(generatedTraceId, options)
  );

  useEffect(() => {
    if (error) {
      latestError.current = error;
    }
  }, [error]);

  useEffect(() => {
    if (
      loading &&
      !POLLING_QUERIES.includes(
        (query.definitions[0] as OperationDefinitionNode)?.name?.value || ''
      )
    ) {
      const token = getSessionToken();
      if (token) {
        emitStudentExtendSessionStorageEvent(token);
      }
    }
  }, [loading, query]);

  return {
    data,
    loading,
    error: !data && !loading && !error ? latestError.current : error,
    ...response,
  };
};

export const useStudentMutation = <
  TData extends any,
  TVariables extends OperationVariables = OperationVariables,
>(
  mutation: DocumentNode,
  options?: MutationHookOptions<TData, TVariables>
): MutationTuple<TData, TVariables> => {
  const generatedTraceId = useMemo(() => generateTraceId(), []);

  const [apolloMutation, apolloResponse] = useMutation<TData, TVariables>(
    mutation,
    setupMutationOptions(generatedTraceId, options)
  );

  useEffect(() => {
    if (apolloResponse.loading) {
      const token = getSessionToken();
      if (token) {
        emitStudentExtendSessionStorageEvent(token);
      }
    }
  }, [apolloResponse]);

  return [apolloMutation, apolloResponse];
};

export const useStudentLazyQuery = <
  TData extends any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode,
  options?: QueryHookOptions<TData, TVariables>
): QueryTuple<TData, TVariables> => {
  const generatedTraceId = useMemo(() => generateTraceId(), []);

  const [apolloQuery, { loading, ...restOptions }] = useLazyQuery<
    TData,
    TVariables
  >(query, setupOptions(generatedTraceId, options));

  const queryName = (query.definitions[0] as OperationDefinitionNode)?.name
    ?.value;

  useEffect(() => {
    if (loading && queryName !== 'ExtendSession') {
      const token = getSessionToken();
      if (token) {
        emitStudentExtendSessionStorageEvent(token);
      }
    }
  }, [loading, queryName]);

  return [apolloQuery, { loading, ...restOptions }];
};
